import { useState } from 'react'
import { Camera, ArrowRight, Star, Sparkles, ChevronRight, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'

type AestheticView = 'split' | 'playful' | 'elegant' | 'memphis' | 'midcentury'

export default function AestheticComparisonPage() {
  const [activeView, setActiveView] = useState<AestheticView>('split')

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <h1 className="text-xl font-bold">Aesthetic Comparison</h1>
          <div className="flex items-center gap-1 p-1 bg-muted rounded-full">
            <button
              onClick={() => setActiveView('split')}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-full transition-all",
                activeView === 'split' ? "bg-background shadow-sm" : "hover:bg-background/50"
              )}
            >
              All
            </button>
            <button
              onClick={() => setActiveView('playful')}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-full transition-all",
                activeView === 'playful' ? "bg-primary text-primary-foreground" : "hover:bg-background/50"
              )}
            >
              Playful
            </button>
            <button
              onClick={() => setActiveView('elegant')}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-full transition-all",
                activeView === 'elegant' ? "bg-slate-900 text-white" : "hover:bg-background/50"
              )}
            >
              Elegant
            </button>
            <button
              onClick={() => setActiveView('memphis')}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-full transition-all",
                activeView === 'memphis' ? "bg-[#FF6EC7] text-black" : "hover:bg-background/50"
              )}
            >
              Memphis
            </button>
            <button
              onClick={() => setActiveView('midcentury')}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-full transition-all",
                activeView === 'midcentury' ? "bg-[#5C4033] text-[#D4A959]" : "hover:bg-background/50"
              )}
            >
              Mid-Century
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className={cn(
        "container mx-auto px-4 py-8",
        activeView === 'split' && "grid lg:grid-cols-4 gap-4"
      )}>
        {(activeView === 'split' || activeView === 'playful') && (
          <div>
            <PlayfulAesthetic compact={activeView === 'split'} />
          </div>
        )}
        {(activeView === 'split' || activeView === 'elegant') && (
          <div>
            <ElegantAesthetic compact={activeView === 'split'} />
          </div>
        )}
        {(activeView === 'split' || activeView === 'memphis') && (
          <div>
            <MemphisAesthetic compact={activeView === 'split'} />
          </div>
        )}
        {(activeView === 'split' || activeView === 'midcentury') && (
          <div>
            <MidCenturyAesthetic compact={activeView === 'split'} />
          </div>
        )}
      </div>

      {/* Memphis Design History Section */}
      {activeView === 'memphis' && (
        <div className="container mx-auto px-4 pb-12">
          <div className="max-w-3xl mx-auto bg-black text-white p-8 rounded-none border-4 border-[#FF6EC7]">
            <h2 className="text-2xl font-black uppercase tracking-tight mb-4 flex items-center gap-3">
              <span className="inline-block w-4 h-4 bg-[#00FFFF] rotate-45" />
              About Memphis Design
              <span className="inline-block w-4 h-4 bg-[#FFFF00] rounded-full" />
            </h2>
            <div className="space-y-4 text-gray-300">
              <p>
                The <strong className="text-[#FF6EC7]">Memphis Design Movement</strong> was born on December 11, 1980,
                in the living room of Italian architect Ettore Sottsass in Milan. The name came from Bob Dylan's song
                "Stuck Inside of Mobile with the Memphis Blues Again" which played repeatedly during their first meeting.
              </p>
              <p>
                Memphis designers rejected the minimalist "good taste" of modernism and the Bauhaus principle of
                "form follows function." Instead, they embraced bold colors, asymmetrical shapes, and playful patterns
                that prioritized emotional impact over utility.
              </p>
              <p className="text-[#00FFFF]">
                Key characteristics: Clashing neon and pastel colors, geometric patterns (squiggles, dots, zigzags),
                terrazzo surfaces, black and white stripes, and asymmetrical compositions.
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Mid-Century Modern History Section */}
      {activeView === 'midcentury' && (
        <div className="container mx-auto px-4 pb-12">
          <div className="max-w-3xl mx-auto bg-[#F5F1E6] p-8 border-l-4 border-l-[#C45C26] relative">
            {/* Wood grain accent */}
            <div className="absolute top-0 right-0 w-24 h-2 bg-gradient-to-r from-[#8B5A2B] via-[#A0522D] to-[#8B5A2B]" />

            <h2 className="text-2xl font-medium tracking-tight mb-4 text-[#5C4033]">
              About Mid-Century Modern
            </h2>
            <div className="space-y-4 text-[#5C4033]/80 leading-relaxed">
              <p>
                <strong className="text-[#5C4033] font-semibold">Frank Lloyd Wright's</strong> philosophy of organic
                architecture emphasized harmony between human habitation and nature. His Prairie School and Usonian
                houses pioneered open floor plans, horizontal lines, and integration with the landscape.
              </p>
              <p>
                Wright mentored talents like Richard Neutra and Rudolph Schindler, laying the foundation for
                Mid-Century Modern design. The movement championed democratic, accessible design using natural
                materials like wood, stone, and concrete.
              </p>
              <p className="text-[#C45C26] font-medium">
                Key characteristics: Earth tones, natural materials, horizontal emphasis, open floor plans,
                large windows connecting interior to exterior, and forms that respond to function and site.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// FRESH & PLAYFUL AESTHETIC
// ============================================
function PlayfulAesthetic({ compact }: { compact?: boolean }) {
  return (
    <div className={cn("space-y-8", compact && "space-y-6")}>
      {/* Title */}
      <div className="text-center">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4 border border-primary/20">
          <Sparkles className="h-4 w-4" />
          Fresh & Playful
        </span>
        <p className="text-muted-foreground text-sm">Warm coral tones, rounded shapes, playful animations</p>
      </div>

      {/* Color Palette */}
      <Section title="Color Palette">
        <div className="flex gap-2">
          <ColorSwatch color="bg-gradient-to-br from-[#F97066] to-[#F97066]/80" label="Coral" />
          <ColorSwatch color="bg-gradient-to-br from-[#2DD4BF] to-[#2DD4BF]/80" label="Teal" />
          <ColorSwatch color="bg-gradient-to-br from-[#FACC15] to-[#FACC15]/80" label="Yellow" />
          <ColorSwatch color="bg-gradient-to-br from-muted to-muted/80" label="Muted" />
        </div>
      </Section>

      {/* Typography */}
      <Section title="Typography">
        <div className="space-y-3">
          <h1 className={cn("font-bold tracking-tight", compact ? "text-2xl" : "text-4xl")}>
            Bold & <span className="text-gradient">Expressive</span>
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Bricolage Grotesque headings with Source Sans 3 body text.
          </p>
        </div>
      </Section>

      {/* Buttons */}
      <Section title="Buttons">
        <div className="flex flex-wrap gap-2">
          <button className="h-10 px-4 bg-gradient-to-r from-primary to-primary/90 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all text-sm">
            Primary
          </button>
          <button className="h-10 px-4 border-2 border-input bg-background hover:border-primary hover:text-primary rounded-lg font-semibold transition-all text-sm">
            Outline
          </button>
          <button className="h-10 px-4 bg-gradient-to-r from-primary via-accent to-secondary text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all text-sm">
            Gradient
          </button>
        </div>
      </Section>

      {/* Card */}
      <Section title="Card">
        <div className="group bg-card p-5 rounded-2xl border shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 group-hover:scale-110 transition-all">
            <Camera className="h-5 w-5 text-primary" />
          </div>
          <h3 className="font-bold mb-1">Feature Card</h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Rounded corners and playful hover effects.
          </p>
        </div>
      </Section>

      {/* Navigation */}
      <Section title="Navigation">
        <div className="bg-card/95 rounded-2xl border p-2 space-y-1">
          {['Dashboard', 'Drops'].map((item, i) => (
            <div
              key={item}
              className={cn(
                "flex items-center px-3 py-2 rounded-full transition-all text-sm",
                i === 0 ? "bg-primary text-white shadow-md" : "text-muted-foreground hover:bg-muted"
              )}
            >
              <Camera className="mr-2 h-4 w-4" />
              <span className="font-medium">{item}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Hero Preview */}
      <Section title="Hero">
        <div className="relative rounded-2xl overflow-hidden p-6 bg-gradient-mesh">
          <div className="absolute top-2 left-2 w-20 h-20 bg-primary/20 rounded-full blur-2xl" />
          <div className="absolute bottom-2 right-2 w-24 h-24 bg-secondary/20 rounded-full blur-2xl" />
          <div className="relative text-center">
            <h2 className="text-lg font-bold mb-2">Collect <span className="text-gradient">effortlessly</span></h2>
            <button className="h-8 px-4 bg-gradient-to-r from-primary to-primary/90 text-white text-xs font-semibold rounded-lg shadow-md inline-flex items-center gap-1">
              Start <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </Section>
    </div>
  )
}

// ============================================
// MODERN & ELEGANT AESTHETIC
// ============================================
function ElegantAesthetic({ compact }: { compact?: boolean }) {
  return (
    <div className={cn("space-y-8", compact && "space-y-6")}>
      {/* Title */}
      <div className="text-center">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-sm bg-slate-900 text-white text-sm font-light tracking-wide mb-4">
          <Star className="h-3 w-3" />
          Modern & Elegant
        </span>
        <p className="text-slate-500 font-light text-sm">Refined neutrals, sharp geometry, restraint</p>
      </div>

      {/* Color Palette */}
      <Section title="Color Palette" elegant>
        <div className="flex gap-2">
          <ColorSwatch color="bg-slate-900" label="Slate" elegant />
          <ColorSwatch color="bg-slate-600" label="Secondary" elegant />
          <ColorSwatch color="bg-amber-600" label="Gold" elegant />
          <ColorSwatch color="bg-slate-100" label="Muted" elegant />
        </div>
      </Section>

      {/* Typography */}
      <Section title="Typography" elegant>
        <div className="space-y-3">
          <h1 className={cn("font-light tracking-tight text-slate-900", compact ? "text-2xl" : "text-4xl")}>
            Refined & <span className="font-semibold">Sophisticated</span>
          </h1>
          <p className="text-slate-500 font-light text-sm leading-relaxed">
            Clean sans-serif with generous letter-spacing.
          </p>
        </div>
      </Section>

      {/* Buttons */}
      <Section title="Buttons" elegant>
        <div className="flex flex-wrap gap-2">
          <button className="h-10 px-4 bg-slate-900 text-white font-medium rounded-sm hover:bg-slate-800 transition-colors tracking-wide text-sm">
            Primary
          </button>
          <button className="h-10 px-4 border border-slate-300 text-slate-900 font-medium rounded-sm hover:border-slate-900 transition-all tracking-wide text-sm">
            Outline
          </button>
          <button className="h-10 px-4 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-medium rounded-sm hover:from-amber-700 transition-all tracking-wide text-sm">
            Gold
          </button>
        </div>
      </Section>

      {/* Card */}
      <Section title="Card" elegant>
        <div className="group bg-white p-5 rounded-sm border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all">
          <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center mb-3 group-hover:bg-slate-900 transition-colors">
            <Camera className="h-5 w-5 text-slate-600 group-hover:text-white transition-colors" />
          </div>
          <h3 className="font-medium text-slate-900 mb-1 tracking-tight">Feature Card</h3>
          <p className="text-slate-500 text-sm font-light leading-relaxed">
            Sharp corners and subtle borders.
          </p>
        </div>
      </Section>

      {/* Navigation */}
      <Section title="Navigation" elegant>
        <div className="bg-white rounded-sm border border-slate-200 divide-y divide-slate-100">
          {['Dashboard', 'Drops'].map((item, i) => (
            <div
              key={item}
              className={cn(
                "flex items-center justify-between px-3 py-2.5 transition-colors text-sm",
                i === 0 ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              )}
            >
              <div className="flex items-center">
                <Camera className="mr-2 h-4 w-4" />
                <span className="tracking-wide">{item}</span>
              </div>
              <ChevronRight className="h-4 w-4 opacity-50" />
            </div>
          ))}
        </div>
      </Section>

      {/* Hero Preview */}
      <Section title="Hero" elegant>
        <div className="relative rounded-sm overflow-hidden p-6 bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
          <div className="relative text-center">
            <h2 className="text-lg font-light text-slate-900 mb-2">Collect <span className="font-semibold">beautifully</span></h2>
            <button className="h-8 px-4 bg-slate-900 text-white text-xs font-medium rounded-sm inline-flex items-center gap-1 tracking-wide">
              Start <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </Section>
    </div>
  )
}

// ============================================
// MEMPHIS DESIGN AESTHETIC
// ============================================
function MemphisAesthetic({ compact }: { compact?: boolean }) {
  return (
    <div className={cn("space-y-8", compact && "space-y-6")}>
      {/* Title */}
      <div className="text-center relative">
        {/* Decorative shapes */}
        <div className="absolute top-0 left-4 w-3 h-3 bg-[#FFFF00] rotate-45" />
        <div className="absolute top-2 right-6 w-2 h-2 bg-[#00FFFF] rounded-full" />
        <span className="inline-flex items-center gap-2 px-4 py-2 bg-black text-[#FF6EC7] text-sm font-black uppercase tracking-widest mb-4 border-2 border-[#FF6EC7]">
          <Zap className="h-4 w-4" />
          Memphis Design
        </span>
        <p className="text-black font-bold text-sm">Bold shapes, neon colors, anti-functional rebellion</p>
      </div>

      {/* Color Palette */}
      <Section title="Color Palette" memphis>
        <div className="flex gap-2">
          <ColorSwatch color="bg-[#FF6EC7]" label="Hot Pink" memphis />
          <ColorSwatch color="bg-[#00FFFF]" label="Cyan" memphis />
          <ColorSwatch color="bg-[#FFFF00]" label="Yellow" memphis />
          <ColorSwatch color="bg-black" label="Black" memphis />
        </div>
      </Section>

      {/* Pattern Preview */}
      <Section title="Patterns" memphis>
        <div className="grid grid-cols-2 gap-2">
          {/* Terrazzo pattern */}
          <div className="h-16 bg-[#FFF5E6] relative overflow-hidden border-2 border-black">
            <div className="absolute top-1 left-2 w-3 h-1 bg-[#FF6EC7] rotate-12" />
            <div className="absolute top-3 right-4 w-2 h-2 bg-[#00FFFF]" />
            <div className="absolute bottom-2 left-6 w-1.5 h-1.5 bg-[#FFFF00] rotate-45" />
            <div className="absolute top-5 left-10 w-2 h-1 bg-black rotate-[-20deg]" />
            <div className="absolute bottom-4 right-2 w-1 h-3 bg-[#FF6EC7] rotate-45" />
            <div className="absolute top-2 left-4 w-1.5 h-1.5 bg-[#98FF98] rounded-full" />
            <p className="absolute bottom-0.5 left-1 text-[8px] font-bold text-black/50">TERRAZZO</p>
          </div>
          {/* Squiggle pattern */}
          <div className="h-16 bg-[#00FFFF] relative overflow-hidden border-2 border-black">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 50">
              <path d="M0,25 Q10,10 20,25 T40,25 T60,25 T80,25 T100,25" fill="none" stroke="black" strokeWidth="3" />
              <path d="M0,35 Q10,20 20,35 T40,35 T60,35 T80,35 T100,35" fill="none" stroke="#FF6EC7" strokeWidth="2" />
            </svg>
            <p className="absolute bottom-0.5 left-1 text-[8px] font-bold text-black/50">SQUIGGLE</p>
          </div>
        </div>
      </Section>

      {/* Typography */}
      <Section title="Typography" memphis>
        <div className="space-y-3">
          <h1 className={cn("font-black uppercase tracking-tight", compact ? "text-2xl" : "text-4xl")}>
            <span className="text-[#FF6EC7]">BOLD</span> &{' '}
            <span className="text-[#00FFFF] [text-shadow:2px_2px_0_black]">RADICAL</span>
          </h1>
          <p className="text-black font-bold text-sm leading-relaxed">
            Chunky, expressive type that demands attention.
          </p>
        </div>
      </Section>

      {/* Buttons */}
      <Section title="Buttons" memphis>
        <div className="flex flex-wrap gap-2">
          <button className="h-10 px-4 bg-[#FF6EC7] text-black font-black uppercase tracking-wider border-2 border-black shadow-[3px_3px_0_black] hover:shadow-[1px_1px_0_black] hover:translate-x-[2px] hover:translate-y-[2px] transition-all text-sm">
            Primary
          </button>
          <button className="h-10 px-4 bg-[#FFFF00] text-black font-black uppercase tracking-wider border-2 border-black shadow-[3px_3px_0_black] hover:shadow-[1px_1px_0_black] hover:translate-x-[2px] hover:translate-y-[2px] transition-all text-sm">
            Secondary
          </button>
          <button className="h-10 px-4 bg-black text-[#00FFFF] font-black uppercase tracking-wider border-2 border-[#00FFFF] shadow-[3px_3px_0_#00FFFF] hover:shadow-[1px_1px_0_#00FFFF] hover:translate-x-[2px] hover:translate-y-[2px] transition-all text-sm">
            Outline
          </button>
        </div>
      </Section>

      {/* Card */}
      <Section title="Card" memphis>
        <div className="relative bg-[#FFF5E6] p-5 border-4 border-black shadow-[6px_6px_0_black] group">
          {/* Decorative corner shapes */}
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-[#FF6EC7] rotate-45" />
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-[#00FFFF] rounded-full" />

          <div className="w-10 h-10 bg-[#FFFF00] border-2 border-black flex items-center justify-center mb-3 rotate-3 group-hover:rotate-[-3deg] transition-transform">
            <Camera className="h-5 w-5 text-black" />
          </div>
          <h3 className="font-black uppercase text-black mb-1">Feature Card</h3>
          <p className="text-black/70 text-sm font-medium leading-relaxed">
            Hard shadows and geometric accents.
          </p>
        </div>
      </Section>

      {/* Navigation */}
      <Section title="Navigation" memphis>
        <div className="bg-black border-4 border-[#FF6EC7] p-2 space-y-1">
          {['Dashboard', 'Drops'].map((item, i) => (
            <div
              key={item}
              className={cn(
                "flex items-center px-3 py-2 transition-all text-sm font-black uppercase tracking-wider",
                i === 0
                  ? "bg-[#FF6EC7] text-black border-2 border-black"
                  : "text-[#00FFFF] hover:bg-[#00FFFF] hover:text-black border-2 border-transparent hover:border-black"
              )}
            >
              <Camera className="mr-2 h-4 w-4" />
              <span>{item}</span>
              {i === 0 && <span className="ml-auto w-2 h-2 bg-[#FFFF00] rotate-45" />}
            </div>
          ))}
        </div>
      </Section>

      {/* Hero Preview */}
      <Section title="Hero" memphis>
        <div className="relative overflow-hidden p-6 bg-[#00FFFF] border-4 border-black">
          {/* Decorative elements */}
          <div className="absolute top-2 left-2 w-8 h-8 bg-[#FFFF00] rotate-12" />
          <div className="absolute top-4 right-4 w-6 h-6 bg-[#FF6EC7] rounded-full" />
          <div className="absolute bottom-2 left-8 w-4 h-4 bg-black rotate-45" />
          <svg className="absolute bottom-0 right-0 w-20 h-10" viewBox="0 0 80 40">
            <path d="M0,20 Q10,5 20,20 T40,20 T60,20 T80,20" fill="none" stroke="black" strokeWidth="4" />
          </svg>

          <div className="relative text-center">
            <h2 className="text-lg font-black uppercase text-black mb-2 [text-shadow:2px_2px_0_#FF6EC7]">
              COLLECT RADICALLY
            </h2>
            <button className="h-8 px-4 bg-[#FF6EC7] text-black text-xs font-black uppercase border-2 border-black shadow-[3px_3px_0_black] inline-flex items-center gap-1">
              Start <Zap className="h-3 w-3" />
            </button>
          </div>
        </div>
      </Section>
    </div>
  )
}

// ============================================
// PROCEDURAL BACKGROUND GENERATORS
// ============================================

// Generate Mid-Century organic shapes
function MidCenturyOrganic({ seed = 42 }: { seed?: number }) {
  const seededRandom = (n: number) => {
    const x = Math.sin(seed + n) * 10000
    return x - Math.floor(x)
  }

  // Generate boomerang/atomic age curves
  const curves = Array.from({ length: 5 }, (_, i) => {
    const startX = seededRandom(i * 4) * 80 + 10
    const startY = seededRandom(i * 4 + 1) * 80 + 10
    const endX = seededRandom(i * 4 + 2) * 80 + 10
    const endY = seededRandom(i * 4 + 3) * 80 + 10
    const ctrlX = (startX + endX) / 2 + (seededRandom(i * 5) - 0.5) * 40
    const ctrlY = (startY + endY) / 2 + (seededRandom(i * 5 + 1) - 0.5) * 40
    return `M${startX},${startY} Q${ctrlX},${ctrlY} ${endX},${endY}`
  })

  // Wood grain horizontal lines
  const grainLines = Array.from({ length: 20 }, (_, i) => ({
    y: i * 5 + seededRandom(i) * 2,
    offset: seededRandom(i * 2) * 20,
    opacity: 0.03 + seededRandom(i * 3) * 0.05
  }))

  return (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
      {/* Wood grain texture */}
      {grainLines.map((line, i) => (
        <path
          key={`grain-${i}`}
          d={`M0,${line.y} Q25,${line.y + (seededRandom(i * 4) - 0.5) * 3} 50,${line.y} T100,${line.y}`}
          fill="none"
          stroke="#5C4033"
          strokeWidth="0.3"
          opacity={line.opacity}
        />
      ))}
      {/* Organic curves */}
      {curves.map((path, i) => (
        <path
          key={i}
          d={path}
          fill="none"
          stroke={['#C45C26', '#6B7B5A', '#D4A959', '#5C4033'][i % 4]}
          strokeWidth="0.8"
          opacity="0.15"
          strokeLinecap="round"
        />
      ))}
      {/* Atomic dots */}
      {Array.from({ length: 8 }, (_, i) => (
        <circle
          key={`dot-${i}`}
          cx={20 + seededRandom(i * 6) * 60}
          cy={20 + seededRandom(i * 6 + 1) * 60}
          r={1 + seededRandom(i * 6 + 2) * 2}
          fill={['#C45C26', '#D4A959'][i % 2]}
          opacity="0.2"
        />
      ))}
    </svg>
  )
}

// ============================================
// MID-CENTURY MODERN AESTHETIC
// ============================================
function MidCenturyAesthetic({ compact }: { compact?: boolean }) {
  return (
    <div className={cn("space-y-8", compact && "space-y-6")}>
      {/* Title */}
      <div className="text-center relative">
        {/* Horizontal line accent */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-[#C45C26]/30" />
        <span className="relative inline-flex items-center gap-2 px-4 py-2 bg-[#F5F1E6] text-[#5C4033] text-sm font-medium tracking-wide mb-4 border border-[#5C4033]/20">
          <div className="w-4 h-1 bg-[#C45C26]" />
          Mid-Century
        </span>
        <p className="text-[#5C4033] text-sm">Organic architecture. Harmony with nature.</p>
      </div>

      {/* Color Palette */}
      <Section title="Color Palette" midcentury>
        <div className="flex gap-2">
          <ColorSwatch color="bg-[#C45C26]" label="Terracotta" midcentury />
          <ColorSwatch color="bg-[#6B7B5A]" label="Sage" midcentury />
          <ColorSwatch color="bg-[#5C4033]" label="Walnut" midcentury />
          <ColorSwatch color="bg-[#D4A959]" label="Amber" midcentury />
        </div>
      </Section>

      {/* Materials */}
      <Section title="Materials" midcentury>
        <div className="grid grid-cols-2 gap-2">
          {/* Wood grain */}
          <div className="h-14 bg-gradient-to-r from-[#8B5A2B] via-[#A0522D] to-[#8B5A2B] relative overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{ background: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)' }} />
            <p className="absolute bottom-0.5 left-1 text-[8px] font-medium text-white/70 tracking-wide">WOOD</p>
          </div>
          {/* Stone texture */}
          <div className="h-14 bg-[#9A8B7A] relative">
            <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(circle at 20% 30%, #666 1px, transparent 1px), radial-gradient(circle at 80% 70%, #888 1px, transparent 1px)' }} />
            <p className="absolute bottom-0.5 left-1 text-[8px] font-medium text-white/70 tracking-wide">STONE</p>
          </div>
        </div>
      </Section>

      {/* Typography */}
      <Section title="Typography" midcentury>
        <div className="space-y-3">
          <h1 className={cn("font-medium tracking-tight text-[#5C4033]", compact ? "text-2xl" : "text-4xl")}>
            Form &{' '}
            <span className="text-[#C45C26] italic">Nature</span>
          </h1>
          <p className="text-[#5C4033]/70 text-sm leading-relaxed">
            Clean typefaces that honor natural proportions.
          </p>
        </div>
      </Section>

      {/* Buttons */}
      <Section title="Buttons" midcentury>
        <div className="flex flex-wrap gap-2">
          <button className="h-10 px-4 bg-[#5C4033] text-[#F5F1E6] font-medium tracking-wide text-sm hover:bg-[#4A3429] transition-colors">
            Primary
          </button>
          <button className="h-10 px-4 bg-[#F5F1E6] text-[#5C4033] font-medium tracking-wide border border-[#5C4033]/30 text-sm hover:border-[#5C4033] transition-all">
            Outline
          </button>
          <button className="h-10 px-4 bg-[#C45C26] text-white font-medium tracking-wide text-sm hover:bg-[#A84D20] transition-colors">
            Accent
          </button>
        </div>
      </Section>

      {/* Card */}
      <Section title="Card" midcentury>
        <div className="relative bg-[#F5F1E6] p-5 border-l-4 border-l-[#C45C26] group overflow-hidden">
          {/* Subtle procedural wood grain texture */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" viewBox="0 0 100 100" preserveAspectRatio="none">
            {Array.from({ length: 12 }, (_, i) => (
              <path
                key={i}
                d={`M0,${i * 8 + 2} Q25,${i * 8 + (Math.sin(i * 3) * 2)} 50,${i * 8 + 2} T100,${i * 8 + 2}`}
                fill="none"
                stroke="#5C4033"
                strokeWidth="0.4"
              />
            ))}
            {/* Organic curves */}
            <path d="M20,30 Q40,20 60,40 T90,50" fill="none" stroke="#C45C26" strokeWidth="0.3" opacity="0.5" />
            <path d="M10,70 Q30,60 50,80 T85,75" fill="none" stroke="#6B7B5A" strokeWidth="0.3" opacity="0.4" />
          </svg>

          {/* Wood accent bar */}
          <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-r from-transparent via-[#8B5A2B] to-transparent opacity-30" />

          <div className="w-10 h-10 rounded-sm bg-[#6B7B5A] flex items-center justify-center mb-3 relative">
            <Camera className="h-5 w-5 text-[#F5F1E6]" />
          </div>
          <h3 className="font-medium text-[#5C4033] mb-1 tracking-wide relative">Feature Card</h3>
          <p className="text-[#5C4033]/60 text-sm leading-relaxed relative">
            Natural materials. Horizontal emphasis.
          </p>
        </div>
      </Section>

      {/* Navigation */}
      <Section title="Navigation" midcentury>
        <div className="bg-[#F5F1E6] border border-[#5C4033]/20 divide-y divide-[#5C4033]/10">
          {['Dashboard', 'Drops'].map((item, i) => (
            <div
              key={item}
              className={cn(
                "flex items-center px-3 py-2.5 transition-all text-sm font-medium tracking-wide",
                i === 0
                  ? "bg-[#5C4033] text-[#F5F1E6] border-l-4 border-l-[#C45C26]"
                  : "text-[#5C4033] hover:bg-[#5C4033]/5"
              )}
            >
              <Camera className="mr-2 h-4 w-4" />
              <span>{item}</span>
              {i === 0 && <div className="ml-auto w-3 h-1 bg-[#D4A959]" />}
            </div>
          ))}
        </div>
      </Section>

      {/* Procedural Organic Pattern Preview */}
      <Section title="Organic Forms" midcentury>
        <div className="relative h-24 bg-[#F5F1E6] border border-[#5C4033]/20 overflow-hidden">
          <MidCenturyOrganic seed={42} />
          <p className="absolute bottom-1 left-2 text-[8px] font-medium text-[#5C4033]/40 tracking-wide">PROCEDURAL ORGANIC</p>
        </div>
      </Section>

      {/* Hero Preview */}
      <Section title="Hero" midcentury>
        <div className="relative overflow-hidden p-6 bg-[#F5F1E6] border border-[#5C4033]/20">
          {/* Procedural organic background */}
          <MidCenturyOrganic seed={88} />

          {/* Horizontal lines (prairie style) */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#C45C26]" />
          <div className="absolute top-2 left-4 right-4 h-px bg-[#5C4033]/20" />
          <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-[#8B5A2B] via-[#A0522D] to-[#8B5A2B]" />

          {/* Atomic age starburst accent */}
          <svg className="absolute top-4 right-4 w-8 h-8 text-[#D4A959] opacity-30" viewBox="0 0 24 24">
            <path d="M12 0L13 8L21 6L14 11L21 18L13 15L12 24L11 15L3 18L10 11L3 6L11 8Z" fill="currentColor" />
          </svg>

          <div className="relative text-center py-2">
            <h2 className="text-lg font-medium text-[#5C4033] mb-2 tracking-wide">
              Collect <span className="text-[#C45C26] italic">organically</span>
            </h2>
            <button className="h-8 px-4 bg-[#5C4033] text-[#F5F1E6] text-xs font-medium tracking-wide inline-flex items-center gap-1 hover:bg-[#4A3429] transition-colors">
              Begin <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </Section>
    </div>
  )
}

// ============================================
// SHARED COMPONENTS
// ============================================
function Section({
  title,
  children,
  elegant,
  memphis,
  midcentury
}: {
  title: string;
  children: React.ReactNode;
  elegant?: boolean;
  memphis?: boolean;
  midcentury?: boolean;
}) {
  return (
    <div>
      <h2 className={cn(
        "text-sm mb-4",
        elegant && "uppercase tracking-widest text-slate-400 font-light",
        memphis && "uppercase tracking-widest text-black font-black border-b-2 border-black pb-1",
        midcentury && "tracking-wide text-[#5C4033]/70 font-medium border-b border-[#C45C26]/30 pb-1",
        !elegant && !memphis && !midcentury && "font-semibold text-muted-foreground"
      )}>
        {memphis && <span className="inline-block w-2 h-2 bg-[#FF6EC7] mr-2 rotate-45" />}
        {midcentury && <span className="inline-block w-3 h-1 bg-[#C45C26] mr-2" />}
        {title}
      </h2>
      {children}
    </div>
  )
}

function ColorSwatch({
  color,
  label,
  elegant,
  memphis,
  midcentury
}: {
  color: string;
  label: string;
  elegant?: boolean;
  memphis?: boolean;
  midcentury?: boolean;
}) {
  return (
    <div className="flex-1">
      <div className={cn(
        "h-12 mb-2",
        color,
        elegant && "rounded-sm",
        memphis && "border-2 border-black shadow-[2px_2px_0_black]",
        midcentury && "border border-[#5C4033]/20",
        !elegant && !memphis && !midcentury && "rounded-xl"
      )} />
      <p className={cn(
        "text-xs",
        elegant && "text-slate-400 font-light tracking-wide",
        memphis && "text-black font-bold uppercase tracking-wide",
        midcentury && "text-[#5C4033]/70 font-medium tracking-wide",
        !elegant && !memphis && !midcentury && "text-muted-foreground"
      )}>
        {label}
      </p>
    </div>
  )
}
